import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Check, Plus, Trash2, Flame, Calendar as CalIcon,
  ChevronLeft, ChevronRight, Loader2,
  Share2, Edit2, X, Trophy, TrendingUp, Zap,
  Snowflake, Trees, Brain, Dumbbell, Sword, Shield, Sun, Moon,
  ArrowLeft, Paintbrush
} from 'lucide-react';

// ── Viking Constants ──
const VIKING_MESSAGES = [
  "Levanta el martillo, guerrero!",
  "Odin observa tu constancia",
  "El frío forja el acero",
  "Un día más cerca del Valhalla",
  "Las runas marcan tu destino",
  "Tu disciplina es tu espada",
  "Los dioses premian al persistente",
  "Cada check es una victoria",
];

const CATEGORIES = {
  fuerza: { label: 'Fuerza', icon: Dumbbell, color: '#8B2C2C' },
  mente: { label: 'Mente', icon: Brain, color: '#5D7A8C' },
  combustible: { label: 'Combustible', icon: Flame, color: '#D4AF37' },
  templanza: { label: 'Templanza', icon: Snowflake, color: '#A5B8C9' },
  conexion: { label: 'Conexión', icon: Trees, color: '#2E4C3E' },
};

const ARCHETYPES = {
  guerrero: { label: 'Guerrero', icon: Sword, desc: '3-5 hábitos · Consistencia', habits: '3-5' },
  berserker: { label: 'Berserker', icon: Shield, desc: '5-7 hábitos · Superación', habits: '5-7' },
  einherjar: { label: 'Einherjar', icon: Trophy, desc: '7-10+ hábitos · Forja total', habits: '7-10+' },
};

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function getWeekDates(offset = 0) {
  const today = new Date();
  today.setDate(today.getDate() + offset * 7);
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function getMonthDates(year, month) {
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: days }, (_, d) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(d + 1).padStart(2, '0')}`
  );
}

function useValhalla() {
  const [dark, setDark] = useState(() => localStorage.getItem('valhalla_dark') !== 'false');
  const toggle = () => {
    setDark(d => {
      localStorage.setItem('valhalla_dark', !d);
      window.dispatchEvent(new Event('valhalla-theme-change'));
      return !d;
    });
  };
  return { dark, toggle };
}

// ── Theme wrapper ──
function V({ dark, children }) {
  const bg = dark ? 'bg-[#0A0F0D]' : 'bg-[#F5F0E8]';
  const text = dark ? 'text-gray-100' : 'text-gray-900';
  return <div className={`min-h-screen ${bg} ${text} transition-colors duration-300 pb-24`}>{children}</div>;
}

// ── Tablilla del Día ──
function Tablilla({ habits, logs, onToggle, weekOffset, setWeekOffset, dark, stats }) {
  const dates = getWeekDates(weekOffset);
  const today = new Date().toISOString().split('T')[0];
  const logSet = new Set(logs.map(l => `${l.habit_id}_${l.fecha}`));
  const todayHabits = habits.length;
  const todayDone = habits.filter(h => logSet.has(`${h.id}_${today}`)).length;
  const pct = todayHabits > 0 ? Math.round((todayDone / todayHabits) * 100) : 0;
  const bestStreak = stats.reduce((m, s) => Math.max(m, s.streak), 0);
  const msg = VIKING_MESSAGES[Math.floor(Date.now() / 86400000) % VIKING_MESSAGES.length];

  const surface = dark ? 'bg-[#1A2320]' : 'bg-white';
  const border = dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]';
  const muted = dark ? 'text-[#5D7A8C]' : 'text-[#8A7F6B]';
  const gold = '#D4AF37';

  return (
    <div className="space-y-4" data-testid="valhalla-tablilla">
      {/* Progress bar */}
      <div className={`${surface} border ${border} rounded-xl p-4`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs uppercase tracking-widest ${muted}`} style={{ fontFamily: 'Cinzel' }}>
            Hacia el Valhalla
          </span>
          <span className="text-sm font-bold" style={{ color: gold }}>{pct}%</span>
        </div>
        <div className={`h-3 rounded-full ${dark ? 'bg-[#0A0F0D]' : 'bg-[#E8E0D0]'} overflow-hidden`}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, #8B2C2C, ${gold})` }}
          />
        </div>
        <p className={`text-xs mt-2 italic ${muted}`}>"{msg}"</p>
      </div>

      {/* Streak */}
      {bestStreak > 0 && (
        <div className="flex items-center justify-center gap-2 py-1">
          <Flame size={16} style={{ color: gold }} />
          <span className="text-sm font-bold" style={{ color: gold, fontFamily: 'Cinzel' }}>
            Racha {bestStreak} días
          </span>
        </div>
      )}

      {/* Week nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w - 1)}
          className={dark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'}>
          <ChevronLeft size={18} />
        </Button>
        <span className={`text-sm ${muted}`}>{dates[0].slice(5)} — {dates[6].slice(5)}</span>
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w + 1)}
          className={dark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'}>
          <ChevronRight size={18} />
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid gap-1" style={{ gridTemplateColumns: '1fr repeat(7, 38px)' }}>
        <div />
        {dates.map((d, i) => (
          <div key={d} className={`text-center text-[10px] font-medium ${d === today ? 'text-[#D4AF37]' : muted}`}>
            <div>{DAY_NAMES[i]}</div>
            <div className={`text-sm mt-0.5 ${d === today ? 'bg-[#D4AF37]/20 rounded-full w-6 h-6 flex items-center justify-center mx-auto font-bold' : ''}`}>
              {parseInt(d.slice(8))}
            </div>
          </div>
        ))}
      </div>

      {/* Habit rows grouped by category */}
      {Object.entries(CATEGORIES).map(([catKey, cat]) => {
        const catHabits = habits.filter(h => (h.categoria || 'fuerza') === catKey);
        if (catHabits.length === 0) return null;
        const CatIcon = cat.icon;
        return (
          <div key={catKey}>
            <div className={`flex items-center gap-2 mb-1 mt-3`}>
              <CatIcon size={12} style={{ color: cat.color }} />
              <span className={`text-[10px] uppercase tracking-wider ${muted}`}>{cat.label}</span>
            </div>
            {catHabits.map(habit => (
              <div key={habit.id} className="grid gap-1 items-center mb-1" style={{ gridTemplateColumns: '1fr repeat(7, 38px)' }}
                data-testid={`habit-row-${habit.id}`}>
                <div className="flex flex-col truncate pr-1">
                  <span className={`text-sm truncate ${dark ? 'text-gray-200' : 'text-gray-800'}`}>{habit.nombre}</span>
                  {habit.hora && <span className={`text-[10px] ${muted}`}>{habit.hora}</span>}
                </div>
                {dates.map(d => {
                  const isChecked = logSet.has(`${habit.id}_${d}`);
                  const catColor = CATEGORIES[habit.categoria || 'fuerza']?.color || gold;
                  return (
                    <button key={d} onClick={() => onToggle(habit.id, d)}
                      className={`w-[34px] h-[34px] rounded-lg border transition-all duration-200 mx-auto flex items-center justify-center
                        ${isChecked
                          ? 'border-transparent scale-105'
                          : `${dark ? 'border-[#2E4C3E] bg-[#0A0F0D]/50 hover:border-[#5D7A8C]' : 'border-[#D4CFC0] bg-[#F5F0E8]/50 hover:border-[#A5B8C9]'}`
                        }`}
                      style={isChecked ? { backgroundColor: catColor + '25', borderColor: catColor } : {}}
                      data-testid={`toggle-${habit.id}-${d}`}
                    >
                      {isChecked && <Check size={15} style={{ color: catColor }} strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}

      {habits.length === 0 && (
        <div className={`text-center py-12 ${muted}`}>
          <Sword size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm" style={{ fontFamily: 'Cinzel' }}>Forja tu primer hábito, guerrero</p>
        </div>
      )}

      {/* Day shield */}
      {todayHabits > 0 && (
        <div className={`${surface} border ${border} rounded-xl p-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Shield size={18} style={{ color: gold }} />
            <span className="text-sm" style={{ fontFamily: 'Cinzel' }}>Escudo del día</span>
          </div>
          <span className="text-sm font-bold" style={{ color: pct >= 80 ? '#2E4C3E' : pct >= 50 ? gold : '#8B2C2C' }}>
            {todayDone}/{todayHabits} · {pct}%
          </span>
        </div>
      )}
    </div>
  );
}

// ── Yggdrasil Calendar ──
function Yggdrasil({ habits, logs, dark }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedHabit, setSelectedHabit] = useState(null);
  const now = new Date();
  const year = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1).getFullYear();
  const month = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1).getMonth();
  const dates = getMonthDates(year, month);
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const today = now.toISOString().split('T')[0];

  const surface = dark ? 'bg-[#1A2320]' : 'bg-white';
  const border = dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]';
  const muted = dark ? 'text-[#5D7A8C]' : 'text-[#8A7F6B]';

  // Calculate daily completion percentages
  const dayPct = useMemo(() => {
    const m = {};
    const filteredHabits = selectedHabit ? habits.filter(h => h.id === selectedHabit) : habits;
    const filteredLogs = selectedHabit ? logs.filter(l => l.habit_id === selectedHabit) : logs;
    dates.forEach(d => {
      const doneCount = filteredLogs.filter(l => l.fecha === d).length;
      m[d] = filteredHabits.length > 0 ? doneCount / filteredHabits.length : 0;
    });
    return m;
  }, [dates, habits, logs, selectedHabit]);

  const getColor = (pct) => {
    if (pct === 0) return dark ? '#1A2320' : '#F5F0E8';
    if (pct < 0.5) return dark ? '#2E1A1A' : '#F0D8D0';
    if (pct < 0.8) return dark ? '#3D3520' : '#E8D8A0';
    return dark ? '#1A3020' : '#C8E0B8';
  };

  // Stats
  const epicDays = Object.values(dayPct).filter(p => p >= 0.8).length;
  const battleDays = Object.values(dayPct).filter(p => p >= 0.5 && p < 0.8).length;

  return (
    <div data-testid="valhalla-yggdrasil" className="space-y-4">
      {/* Habit filter */}
      {habits.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button onClick={() => setSelectedHabit(null)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${!selectedHabit ? 'font-bold' : 'opacity-60'}`}
            style={{ backgroundColor: !selectedHabit ? '#D4AF37' : (dark ? '#1A2320' : '#E8E0D0'), color: !selectedHabit ? '#0A0F0D' : undefined }}>
            Todos
          </button>
          {habits.map(h => (
            <button key={h.id} onClick={() => setSelectedHabit(h.id)}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${selectedHabit === h.id ? 'font-bold' : 'opacity-60'}`}
              style={{ backgroundColor: selectedHabit === h.id ? (CATEGORIES[h.categoria]?.color || '#D4AF37') : (dark ? '#1A2320' : '#E8E0D0'), color: selectedHabit === h.id ? '#fff' : undefined }}>
              {h.nombre}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setMonthOffset(m => m - 1)}
          className={dark ? 'text-gray-400' : 'text-gray-600'}><ChevronLeft size={18} /></Button>
        <span className="font-semibold" style={{ fontFamily: 'Cinzel', color: '#D4AF37' }}>{MONTH_NAMES[month]} {year}</span>
        <Button variant="ghost" size="sm" onClick={() => setMonthOffset(m => m + 1)}
          className={dark ? 'text-gray-400' : 'text-gray-600'}><ChevronRight size={18} /></Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] mb-1" style={{ color: dark ? '#5D7A8C' : '#8A7F6B' }}>
        {DAY_NAMES.map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDow }).map((_, i) => <div key={`e-${i}`} />)}
        {dates.map(d => {
          const pct = dayPct[d] || 0;
          const isToday = d === today;
          return (
            <div key={d}
              className={`aspect-square rounded-md flex items-center justify-center text-xs transition-all
                ${isToday ? 'ring-1 ring-[#D4AF37]' : ''}`}
              style={{ backgroundColor: getColor(pct), color: pct >= 0.8 ? (dark ? '#A5E0B0' : '#2E4C3E') : (dark ? '#5D7A8C' : '#8A7F6B'), fontWeight: pct >= 0.8 ? 'bold' : 'normal' }}>
              {parseInt(d.slice(8))}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className={`${surface} border ${border} rounded-xl p-3 space-y-2`}>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ backgroundColor: getColor(0) }} /> <span className={muted}>Día gris</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ backgroundColor: getColor(0.6) }} /> <span className={muted}>Batalla</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ backgroundColor: getColor(1) }} /> <span className={muted}>Épico</span></div>
        </div>
        <div className={`grid grid-cols-2 gap-2 text-xs ${muted}`}>
          <div>Días épicos: <span className="font-bold text-[#2E4C3E]">{epicDays}</span></div>
          <div>Batallas: <span className="font-bold text-[#D4AF37]">{battleDays}</span></div>
        </div>
      </div>
    </div>
  );
}

// ── Festín del Guerrero ──
function Festin({ token, dark }) {
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [mision, setMision] = useState('pre-batalla');
  const [objetivo, setObjetivo] = useState('fuerza');

  const surface = dark ? 'bg-[#1A2320]' : 'bg-white';
  const border = dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]';
  const muted = dark ? 'text-[#5D7A8C]' : 'text-[#8A7F6B]';
  const inputBg = dark ? 'bg-[#0A0F0D] border-[#2E4C3E] text-gray-200' : 'bg-[#F5F0E8] border-[#D4CFC0] text-gray-800';

  const generate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/habits/recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mision, objetivo })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setRecipe(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const shareRecipe = () => {
    if (!recipe) return;
    const text = `${recipe.nombre}\n${recipe.descripcion || ''}\n\n${recipe.calorias} kcal | P:${recipe.proteinas} C:${recipe.carbohidratos} G:${recipe.grasas}\n${recipe.tiempo_prep}\n\nIngredientes:\n${recipe.ingredientes?.map(i => `- ${i}`).join('\n')}\n\nPreparación:\n${recipe.pasos?.map((p, i) => `${i + 1}. ${p}`).join('\n')}\n\n${recipe.poder || ''}\n\n-- MK Valhalla`;
    if (navigator.share) navigator.share({ title: recipe.nombre, text });
    else { navigator.clipboard.writeText(text); alert('Receta copiada'); }
  };

  const misiones = [
    { id: 'pre-batalla', label: 'Pre-batalla', icon: Sword },
    { id: 'post-batalla', label: 'Post-batalla', icon: Shield },
    { id: 'templanza', label: 'Templanza', icon: Snowflake },
    { id: 'forja', label: 'Forja (volumen)', icon: Dumbbell },
    { id: 'claridad', label: 'Claridad mental', icon: Brain },
  ];

  return (
    <div className="space-y-4" data-testid="valhalla-festin">
      <p className={`text-xs uppercase tracking-widest ${muted}`} style={{ fontFamily: 'Cinzel' }}>Selecciona tu misión</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {misiones.map(m => {
          const MIcon = m.icon;
          const sel = mision === m.id;
          return (
            <button key={m.id} onClick={() => setMision(m.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs border transition-all
                ${sel ? 'border-[#D4AF37] font-bold' : `${border} opacity-60 hover:opacity-100`}`}
              style={sel ? { backgroundColor: '#D4AF37' + '15', color: '#D4AF37' } : { backgroundColor: dark ? '#1A2320' : '#fff' }}>
              <MIcon size={14} /> {m.label}
            </button>
          );
        })}
      </div>

      <select value={objetivo} onChange={e => setObjetivo(e.target.value)}
        className={`w-full border rounded-xl px-4 py-3 text-sm ${inputBg}`} data-testid="festin-objetivo">
        <option value="fuerza">Fuerza máxima</option>
        <option value="resistencia">Resistencia</option>
        <option value="recuperación">Recuperación</option>
        <option value="pérdida de grasa">Pérdida de grasa</option>
        <option value="claridad mental">Claridad mental</option>
      </select>

      <Button onClick={generate} disabled={loading}
        className="w-full font-semibold rounded-xl h-12"
        style={{ backgroundColor: '#8B2C2C', color: '#F5F0E8', fontFamily: 'Cinzel' }}
        data-testid="festin-generate-btn">
        {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Invocando a los dioses...</> : 'Generar Festín'}
      </Button>

      {recipe && !recipe.error && (
        <div className={`${surface} border ${border} rounded-xl p-5 space-y-4 animate-in fade-in`}>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Cinzel', color: '#D4AF37' }}>{recipe.nombre}</h3>
              {recipe.descripcion && <p className={`text-xs mt-1 ${muted}`}>{recipe.descripcion}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={shareRecipe}><Share2 size={16} /></Button>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { v: recipe.calorias, l: 'kcal', c: '#D4AF37' },
              { v: recipe.proteinas, l: 'Proteína', c: '#8B2C2C' },
              { v: recipe.carbohidratos, l: 'Carbos', c: '#2E4C3E' },
              { v: recipe.grasas, l: 'Grasas', c: '#5D7A8C' },
            ].map((s, i) => (
              <div key={i} className={`${dark ? 'bg-[#0A0F0D]' : 'bg-[#F5F0E8]'} rounded-lg p-2`}>
                <div className="text-sm font-bold" style={{ color: s.c }}>{s.v}</div>
                <div className={`text-[10px] ${muted}`}>{s.l}</div>
              </div>
            ))}
          </div>
          <div className={`text-xs ${muted}`}>Tiempo: {recipe.tiempo_prep}</div>
          <div>
            <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Cinzel' }}>Ingredientes</h4>
            <ul className="space-y-1">{recipe.ingredientes?.map((ing, i) => (
              <li key={i} className={`text-sm ${muted} flex items-start gap-2`}>
                <span style={{ color: '#D4AF37' }}>•</span> {ing}
              </li>
            ))}</ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Cinzel' }}>Preparación</h4>
            <ol className="space-y-2">{recipe.pasos?.map((p, i) => (
              <li key={i} className={`text-sm ${muted} flex gap-2`}>
                <span style={{ color: '#8B2C2C' }} className="font-bold">{i + 1}.</span> {p}
              </li>
            ))}</ol>
          </div>
          {recipe.poder && (
            <div className="rounded-lg p-3" style={{ backgroundColor: '#D4AF37' + '10', borderLeft: '3px solid #D4AF37' }}>
              <p className="text-sm" style={{ color: '#D4AF37' }}>{recipe.poder}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sagas (Achievements) ──
function Sagas({ token, stats, dark }) {
  const [sagaData, setSagaData] = useState(null);
  const surface = dark ? 'bg-[#1A2320]' : 'bg-white';
  const border = dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]';
  const muted = dark ? 'text-[#5D7A8C]' : 'text-[#8A7F6B]';

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/habits/sagas`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setSagaData).catch(() => {});
  }, [token]);

  if (!sagaData) return <div className={`text-center py-8 ${muted}`}>Cargando sagas...</div>;

  return (
    <div className="space-y-4" data-testid="valhalla-sagas">
      {/* Main sagas */}
      {sagaData.sagas.map(s => {
        const pct = Math.min(100, Math.round((s.progreso / s.dias) * 100));
        const done = s.completada;
        return (
          <div key={s.id} className={`${surface} border ${border} rounded-xl p-4 ${done ? 'ring-1 ring-[#D4AF37]' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trophy size={18} style={{ color: done ? '#D4AF37' : (dark ? '#2E4C3E' : '#D4CFC0') }} />
                <div>
                  <span className="font-semibold" style={{ fontFamily: 'Cinzel', color: done ? '#D4AF37' : undefined }}>
                    Nivel {s.nivel} — {s.nombre}
                  </span>
                  <div className={`text-xs ${muted}`}>{s.dias} días seguidos</div>
                </div>
              </div>
              {done && <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] font-bold">Completada</span>}
            </div>
            <div className={`h-2 rounded-full ${dark ? 'bg-[#0A0F0D]' : 'bg-[#E8E0D0]'} overflow-hidden`}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, backgroundColor: done ? '#D4AF37' : '#8B2C2C' }} />
            </div>
            <div className={`text-xs mt-1 ${muted}`}>{s.progreso}/{s.dias} días ({pct}%)</div>
          </div>
        );
      })}

      {/* Special achievements */}
      <div className="mt-6">
        <p className={`text-xs uppercase tracking-widest mb-3 ${muted}`} style={{ fontFamily: 'Cinzel' }}>Logros especiales</p>
        <div className="grid grid-cols-1 gap-2">
          {sagaData.especiales.map(e => {
            const pct = Math.min(100, Math.round((e.progreso / e.meta) * 100));
            const catData = CATEGORIES[e.categoria];
            return (
              <div key={e.id} className={`${surface} border ${border} rounded-lg p-3 flex items-center gap-3`}>
                {catData && <catData.icon size={16} style={{ color: catData.color }} />}
                <div className="flex-1">
                  <div className="flex justify-between text-sm">
                    <span>{e.nombre}</span>
                    <span className={`font-bold ${muted}`}>{e.progreso}/{e.meta}</span>
                  </div>
                  <div className={`h-1.5 rounded-full mt-1 ${dark ? 'bg-[#0A0F0D]' : 'bg-[#E8E0D0]'}`}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: catData?.color || '#D4AF37' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Habit Form Modal ──
function HabitForm({ show, onClose, onSave, editData, dark }) {
  const [form, setForm] = useState({ nombre: '', descripcion: '', icono: 'check', color: '#D4AF37', categoria: 'fuerza', dificultad: 'guerrero', hora: '' });
  const surface = dark ? 'bg-[#1A2320]' : 'bg-white';
  const inputBg = dark ? 'bg-[#0A0F0D] border-[#2E4C3E] text-gray-200' : 'bg-[#F5F0E8] border-[#D4CFC0] text-gray-800';
  const muted = dark ? 'text-[#5D7A8C]' : 'text-[#8A7F6B]';

  useEffect(() => {
    if (editData) {
      setForm({
        nombre: editData.nombre || '', descripcion: editData.descripcion || '',
        icono: editData.icono || 'check', color: editData.color || '#D4AF37',
        categoria: editData.categoria || 'fuerza', dificultad: editData.dificultad || 'guerrero',
        hora: editData.hora || ''
      });
    } else {
      setForm({ nombre: '', descripcion: '', icono: 'check', color: '#D4AF37', categoria: 'fuerza', dificultad: 'guerrero', hora: '' });
    }
  }, [editData, show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" data-testid="habit-form-modal">
      <div className={`${surface} rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 border ${dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]'} max-h-[85vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold" style={{ fontFamily: 'Cinzel', color: '#D4AF37' }}>
            {editData ? 'Forjar hábito' : 'Nuevo hábito vikingo'}
          </h3>
          <button onClick={onClose} className={`${muted} hover:opacity-100`}><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <input type="text" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
            placeholder="Ej: Exposición al frío" className={`w-full border rounded-xl px-4 py-3 ${inputBg}`}
            autoFocus data-testid="habit-name-input" />

          <input type="text" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
            placeholder="Descripción (opcional)" className={`w-full border rounded-xl px-4 py-2.5 text-sm ${inputBg}`} />

          <div>
            <label className={`text-xs mb-2 block ${muted}`}>Categoría</label>
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5">
              {Object.entries(CATEGORIES).map(([k, v]) => {
                const CI = v.icon;
                return (
                  <button key={k} onClick={() => setForm(f => ({ ...f, categoria: k }))}
                    className={`flex items-center gap-1 px-2 py-2 rounded-lg text-[11px] border transition-all
                      ${form.categoria === k ? 'font-bold border-[#D4AF37]' : `opacity-60 ${dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]'}`}`}
                    style={form.categoria === k ? { backgroundColor: v.color + '15', color: v.color } : {}}>
                    <CI size={13} /> {v.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={`text-xs mb-2 block ${muted}`}>Exigencia</label>
            <div className="flex gap-2">
              {Object.entries(ARCHETYPES).map(([k, v]) => {
                const AI = v.icon;
                return (
                  <button key={k} onClick={() => setForm(f => ({ ...f, dificultad: k }))}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs border transition-all
                      ${form.dificultad === k ? 'font-bold border-[#D4AF37] text-[#D4AF37]' : `opacity-60 ${dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]'}`}`}
                    style={form.dificultad === k ? { backgroundColor: '#D4AF37' + '10' } : {}}>
                    <AI size={13} /> {v.label}
                  </button>
                );
              })}
            </div>
          </div>

          <input type="time" value={form.hora} onChange={e => setForm(f => ({ ...f, hora: e.target.value }))}
            className={`w-full border rounded-xl px-4 py-2.5 text-sm ${inputBg}`} />

          <Button onClick={() => { if (form.nombre.trim()) onSave(form); }}
            className="w-full font-bold rounded-xl h-12"
            style={{ backgroundColor: '#D4AF37', color: '#0A0F0D', fontFamily: 'Cinzel' }}
            data-testid="save-habit-btn">
            {editData ? 'Actualizar' : 'Forjar hábito'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──
export default function MKHabits() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { dark, toggle: toggleTheme } = useValhalla();
  const [globalTheme, setGlobalTheme] = useState(() => localStorage.getItem('valhalla_global') === 'true');
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [tab, setTab] = useState('tablilla');
  const [weekOffset, setWeekOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [archetype, setArchetype] = useState({ arquetipo: 'guerrero', nombre_vikingo: '' });

  const fetchAll = useCallback(async () => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    const [hR, lR, sR, aR] = await Promise.all([
      fetch(`${API}/habits`, { headers: h }),
      fetch(`${API}/habits/logs?desde=${getWeekDates(-8)[0]}&hasta=${getWeekDates(1)[6]}`, { headers: h }),
      fetch(`${API}/habits/stats`, { headers: h }),
      fetch(`${API}/habits/archetype`, { headers: h }),
    ]);
    if (hR.ok) setHabits(await hR.json());
    if (lR.ok) setLogs(await lR.json());
    if (sR.ok) setStats(await sR.json());
    if (aR.ok) setArchetype(await aR.json());
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Apply global theme to document
  useEffect(() => {
    if (globalTheme) {
      document.documentElement.classList.add('valhalla-theme');
    } else {
      document.documentElement.classList.remove('valhalla-theme');
    }
  }, [globalTheme]);

  const toggleGlobalTheme = () => {
    const newVal = !globalTheme;
    setGlobalTheme(newVal);
    localStorage.setItem('valhalla_global', newVal);
    if (newVal) {
      document.documentElement.classList.add('valhalla-theme');
    } else {
      document.documentElement.classList.remove('valhalla-theme');
    }
  };

  const toggleHabit = async (habitId, fecha) => {
    const res = await fetch(`${API}/habits/${habitId}/toggle`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ fecha })
    });
    if (res.ok) {
      const data = await res.json();
      if (data.completed) setLogs(p => [...p, { habit_id: habitId, fecha }]);
      else setLogs(p => p.filter(l => !(l.habit_id === habitId && l.fecha === fecha)));
      fetch(`${API}/habits/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setStats).catch(() => {});
    }
  };

  const saveHabit = async (form) => {
    const method = editData ? 'PUT' : 'POST';
    const url = editData ? `${API}/habits/${editData.id}` : `${API}/habits`;
    await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
    setShowForm(false); setEditData(null); fetchAll();
  };

  const deleteHabit = async (id) => {
    if (!window.confirm('¿Eliminar este hábito y su historial?')) return;
    await fetch(`${API}/habits/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchAll();
  };

  const bestStreak = stats.reduce((m, s) => Math.max(m, s.streak), 0);
  const arcIcon = ARCHETYPES[archetype.arquetipo]?.icon || Sword;
  const ArcIcon = arcIcon;
  const surface = dark ? 'bg-[#1A2320]' : 'bg-white';
  const border = dark ? 'border-[#2E4C3E]' : 'border-[#D4CFC0]';
  const muted = dark ? 'text-[#5D7A8C]' : 'text-[#8A7F6B]';

  const tabs = [
    { id: 'tablilla', label: 'Tablilla', icon: Sword },
    { id: 'yggdrasil', label: 'Yggdrasil', icon: Trees },
    { id: 'festin', label: 'Festín', icon: Flame },
    { id: 'sagas', label: 'Sagas', icon: Trophy },
  ];

  return (
    <V dark={dark}>
      <div className="max-w-lg mx-auto px-4 py-6" data-testid="mk-habits-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)}
              className={`p-2 rounded-lg ${dark ? 'text-gray-400 hover:text-[#D4AF37] hover:bg-[#1A2320]' : 'text-gray-500 hover:text-[#D4AF37] hover:bg-[#E8E0D0]'} transition-all`}
              data-testid="valhalla-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-black" style={{ fontFamily: 'Cinzel', color: '#D4AF37' }}>
                MK Valhalla
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <ArcIcon size={14} style={{ color: '#D4AF37' }} />
                <span className={`text-xs uppercase tracking-wider ${muted}`}>
                  {ARCHETYPES[archetype.arquetipo]?.label || 'Guerrero'} · {archetype.nombre_vikingo || user?.nombre}
                  {bestStreak > 0 && <> · Racha {bestStreak}</>}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={toggleGlobalTheme}
              className={`p-2 rounded-lg transition-all ${globalTheme ? 'text-[#D4AF37] bg-[#D4AF37]/10' : `${muted} hover:opacity-80`}`}
              title={globalTheme ? 'Tema vikingo global activado' : 'Activar tema vikingo en toda la app'}
              data-testid="global-theme-toggle">
              <Paintbrush size={16} />
            </button>
            <button onClick={toggleTheme} className={`p-2 rounded-lg ${dark ? 'text-[#D4AF37]' : 'text-[#8A7F6B]'} hover:opacity-80`}
              data-testid="theme-toggle">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Button onClick={() => { setShowForm(true); setEditData(null); }}
              className="rounded-full h-9 w-9 p-0"
              style={{ backgroundColor: '#D4AF37', color: '#0A0F0D' }}
              data-testid="add-habit-btn">
              <Plus size={18} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 ${surface} border ${border} rounded-xl p-1 mb-5 mt-4`}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs transition-all
                ${tab === t.id ? 'font-bold shadow-sm' : `${muted} hover:opacity-80`}`}
              style={tab === t.id ? { backgroundColor: dark ? '#0A0F0D' : '#F5F0E8', color: '#D4AF37', fontFamily: 'Cinzel' } : {}}
              data-testid={`tab-${t.id}`}>
              <t.icon size={14} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'tablilla' && (
          <>
            <Tablilla habits={habits} logs={logs} onToggle={toggleHabit} weekOffset={weekOffset} setWeekOffset={setWeekOffset} dark={dark} stats={stats} />
            {habits.length > 0 && (
              <div className="mt-6 space-y-2">
                <p className={`text-[10px] uppercase tracking-widest ${muted}`} style={{ fontFamily: 'Cinzel' }}>Gestionar hábitos</p>
                {habits.map(h => {
                  const cat = CATEGORIES[h.categoria || 'fuerza'];
                  const CIcon = cat?.icon || Check;
                  return (
                    <div key={h.id} className={`flex items-center justify-between ${surface} border ${border} rounded-lg px-3 py-2`}>
                      <div className="flex items-center gap-2">
                        <CIcon size={14} style={{ color: cat?.color }} />
                        <span className="text-sm">{h.nombre}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditData(h); setShowForm(true); }} className={`p-1.5 ${muted} hover:text-[#D4AF37]`} data-testid={`edit-habit-${h.id}`}><Edit2 size={14} /></button>
                        <button onClick={() => deleteHabit(h.id)} className={`p-1.5 ${muted} hover:text-[#8B2C2C]`} data-testid={`delete-habit-${h.id}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === 'yggdrasil' && <Yggdrasil habits={habits} logs={logs} dark={dark} />}
        {tab === 'festin' && <Festin token={token} dark={dark} />}
        {tab === 'sagas' && <Sagas token={token} stats={stats} dark={dark} />}

        <HabitForm show={showForm} onClose={() => { setShowForm(false); setEditData(null); }} onSave={saveHabit} editData={editData} dark={dark} />
      </div>
    </V>
  );
}
